import jwtDecode from "jwt-decode";
import {
  createContext,
  ReactNode,
  useEffect,
  useReducer,
  useRef,
  useState,
} from "react";
import LoadingScreen from "../components/LoadingScreen";
import axiosInstance from "../api";
import axios from "axios";
import { ACCESS_TOKEN_KEY, API_BASE_URL, REFRESH_TOKEN_KEY, USER_KEY } from "../api/constants";

export type ActionMap<M extends { [index: string]: any }> = {
  [Key in keyof M]: M[Key] extends undefined
    ? {
        type: Key;
      }
    : {
        type: Key;
        payload: M[Key];
      };
};

export type AuthUser = null | Record<string, any>;

export type AuthState = {
  isAuthenticated: boolean;
  isInitialized: boolean;
  user: AuthUser;
};

enum Types {
  Init = "INIT",
  Login = "LOGIN",
  Logout = "LOGOUT",
  UpdateUser = "UPDATE_USER",
}

type JWTAuthPayload = {
  [Types.Init]: {
    isAuthenticated: boolean;
    user: AuthUser;
  };
  [Types.Logout]: undefined;
  [Types.Login]: { user: AuthUser };
  [Types.UpdateUser]: { user: AuthUser };
};

type JWTActions = ActionMap<JWTAuthPayload>[keyof ActionMap<JWTAuthPayload>];

type TAuthContext = {
  user?: AuthUser | null;
  isInitialized?: boolean;
  isAuthenticated?: boolean;
  method?: string;
  loginWithEmailPassword?: (email: string, password: string) => Promise<void>;
  requestOtp?: (phone_number: string, purpose: string) => Promise<void>;
  loginWithOtp?: (phone_number: string, otp_code: string) => Promise<void>;
  logout?: () => void;
  refreshUser?: () => Promise<void>;
};

const initialState: AuthState = {
  user: null,
  isInitialized: false,
  isAuthenticated: false,
};

const isValidToken = (accessToken: string) => {
  if (!accessToken) return false;
  try {
    const decodedToken = jwtDecode<{ exp: number }>(accessToken);
    const currentTime = Date.now() / 1000;
    return decodedToken.exp > currentTime;
  } catch (error) {
    console.error("Invalid token:", error);
    return false;
  }
};

const setSession = (accessToken: string | null, refreshToken: string | null = null) => {
  if (accessToken) {
    localStorage.setItem(ACCESS_TOKEN_KEY, accessToken);
    axiosInstance.defaults.headers.common.Authorization = `Bearer ${accessToken}`;
    if (refreshToken) {
      localStorage.setItem(REFRESH_TOKEN_KEY, refreshToken);
    }
  } else {
    localStorage.removeItem(ACCESS_TOKEN_KEY);
    localStorage.removeItem(REFRESH_TOKEN_KEY);
    localStorage.removeItem(USER_KEY);
    delete axiosInstance.defaults.headers.common.Authorization;
  }
};

const reducer = (state: AuthState, action: JWTActions) => {
  switch (action.type) {
    case "INIT": {
      return {
        isInitialized: true,
        user: action.payload.user,
        isAuthenticated: action.payload.isAuthenticated,
      };
    }
    case "LOGIN": {
      return {
        ...state,
        isAuthenticated: true,
        user: action.payload.user,
      };
    }
    case "UPDATE_USER": {
      return {
        ...state,
        user: action.payload.user,
      };
    }
    case "LOGOUT": {
      return {
        ...state,
        user: null,
        isAuthenticated: false,
      };
    }
    default: {
      return state;
    }
  }
};

const AuthContext = createContext<TAuthContext>({
  user: null,
  isInitialized: false,
  isAuthenticated: false,
  method: "JWT",
  loginWithEmailPassword: () => Promise.resolve(),
  requestOtp: () => Promise.resolve(),
  loginWithOtp: () => Promise.resolve(),
  logout: () => {},
  refreshUser: () => Promise.resolve(),
});

type AuthProviderProps = {
  children: ReactNode;
};

export const AuthProvider = ({ children }: AuthProviderProps) => {
  const [state, dispatch] = useReducer(reducer, initialState);
  const [, setGetUserDetails] = useState(false);
  const isRefreshing = useRef(false);
  const failedQueue = useRef<any[]>([]);

  const processQueue = (error: any, token: string | null = null) => {
    failedQueue.current.forEach((prom) => {
      if (error) {
        prom.reject(error);
      } else {
        prom.resolve(token);
      }
    });
    failedQueue.current = [];
  };

  const refreshTokenFn = async () => {
    try {
      const refreshToken = localStorage.getItem(REFRESH_TOKEN_KEY);
      if (!refreshToken) {
        throw new Error("No refresh token available");
      }

      console.log("🔄 Attempting token refresh...");
      
      const response = await axios.post(`${API_BASE_URL}auth/token/refresh/`, {
        refresh: refreshToken,
      });

      const { access } = response.data;

      if (!access) {
        throw new Error("No access token in refresh response");
      }

      console.log("✅ Token refresh successful");
      
      // CRITICAL: Update session with new token
      setSession(access, refreshToken);
      
      return access;
    } catch (error) {
      console.error("❌ Token refresh failed:", error);
      setSession(null);
      throw error;
    }
  };

  const loginWithEmailPassword = async (email: string, password: string) => {
    try {
      const response = await axios.post(`${API_BASE_URL}auth/login/`, {
        email,
        password,
      });

      const { tokens, user } = response.data;
      setSession(tokens.access, tokens.refresh);
      
      localStorage.setItem(USER_KEY, JSON.stringify(user));

      dispatch({
        type: Types.Login,
        payload: { user },
      });
      setGetUserDetails(true);
    } catch (error: any) {
      throw new Error(error.response?.data?.detail || "Login failed");
    }
  };

  const requestOtp = async (phone_number: string, purpose: string) => {
    try {
      await axios.post(`${API_BASE_URL}auth/request-otp/`, {
        phone_number,
        purpose,
      });
    } catch (error: any) {
      throw new Error(error.response?.data?.message || "Failed to request OTP");
    }
  };

  const loginWithOtp = async (phone_number: string, otp_code: string) => {
    try {
      const response = await axios.post(`${API_BASE_URL}auth/login/`, {
        phone_number,
        otp_code,
      });

      const { access, refresh, user } = response.data;
      setSession(access, refresh);
      localStorage.setItem(USER_KEY, JSON.stringify(user));

      dispatch({
        type: Types.Login,
        payload: { user },
      });
      setGetUserDetails(true);
    } catch (error: any) {
      throw new Error(error.response?.data?.message || "Login failed");
    }
  };

  const logout = () => {
    setSession(null);
    setGetUserDetails(false);
    dispatch({ type: Types.Logout });
  };

  const getUser = async (accessToken: string): Promise<any> => {
    try {
      const storedUser = localStorage.getItem(USER_KEY);
      if (storedUser) {
        return JSON.parse(storedUser);
      }

      const decodedToken: any = jwtDecode<{ user_id: string }>(accessToken);
      const userId = decodedToken.user_id;

      const response = await axiosInstance.get(`/auth/users/${userId}/`);
      const user = response.data;
      
      localStorage.setItem(USER_KEY, JSON.stringify(user));
      return user;
    } catch (error: any) {
      console.error("Failed to fetch user:", error);
      return {};
    }
  };

  const refreshUser = async () => {
    try {
      const accessToken = window.localStorage.getItem(ACCESS_TOKEN_KEY);
      if (!accessToken || !isValidToken(accessToken)) {
        console.error("No valid access token available");
        return;
      }

      const response = await axiosInstance.get('/auth/users/me/');
      const user = response.data;

      localStorage.setItem(USER_KEY, JSON.stringify(user));

      dispatch({
        type: Types.UpdateUser,
        payload: { user },
      });
    } catch (error: any) {
      console.error("Failed to refresh user:", error);
      if (error.response?.status === 401) {
        const newAccessToken = await refreshTokenFn();
        if (newAccessToken) {
          try {
            const response = await axiosInstance.get('/auth/users/me/');
            const user = response.data;
            localStorage.setItem(USER_KEY, JSON.stringify(user));
            dispatch({
              type: Types.UpdateUser,
              payload: { user },
            });
          } catch (retryError) {
            console.error("Failed to refresh user after token refresh:", retryError);
            logout();
          }
        } else {
          logout();
        }
      }
    }
  };

  useEffect(() => {
    (async () => {
      try {
        const accessToken = window.localStorage.getItem(ACCESS_TOKEN_KEY);

        if (accessToken && isValidToken(accessToken)) {
          const user = await getUser(accessToken);
          dispatch({
            type: Types.Init,
            payload: {
              user,
              isAuthenticated: true,
            },
          });
        } else if (localStorage.getItem(REFRESH_TOKEN_KEY)) {
          const newAccessToken = await refreshTokenFn();
          if (newAccessToken) {
            const user = await getUser(newAccessToken);
            dispatch({
              type: Types.Init,
              payload: {
                user,
                isAuthenticated: true,
              },
            });
          } else {
            dispatch({
              type: Types.Init,
              payload: {
                user: null,
                isAuthenticated: false,
              },
            });
          }
        } else {
          dispatch({
            type: Types.Init,
            payload: {
              user: null,
              isAuthenticated: false,
            },
          });
        }
      } catch (err) {
        console.error("Initialization error:", err);
        setSession(null);
        dispatch({
          type: Types.Init,
          payload: {
            user: null,
            isAuthenticated: false,
          },
        });
      }
    })();
  }, []);

  // IMPROVED Token refresh interceptor
  useEffect(() => {
    const interceptor = axiosInstance.interceptors.response.use(
      (response) => response,
      async (error) => {
        const originalRequest = error.config;

        // Don't retry these cases
        if (
          !error.response ||
          originalRequest._retry ||
          originalRequest.url?.includes('/auth/login') ||
          originalRequest.url?.includes('/auth/token/refresh')
        ) {
          return Promise.reject(error);
        }

        // Only handle 401 errors
        if (error.response.status === 401) {
          const refreshToken = localStorage.getItem(REFRESH_TOKEN_KEY);

          if (!refreshToken) {
            console.log("❌ No refresh token, logging out");
            setSession(null);
            dispatch({ type: Types.Logout });

            if (window.location.pathname !== "/login") {
              window.location.href = "/login";
            }
            return Promise.reject(error);
          }

          // Mark as retried BEFORE checking if refreshing
          originalRequest._retry = true;

          // If already refreshing, queue this request
          if (isRefreshing.current) {
            console.log("⏳ Queueing request while refresh in progress");
            return new Promise((resolve, reject) => {
              failedQueue.current.push({ resolve, reject });
            })
              .then((token) => {
                console.log("✅ Retrying queued request with new token");
                originalRequest.headers.Authorization = `Bearer ${token}`;
                return axiosInstance(originalRequest);
              })
              .catch((err) => {
                return Promise.reject(err);
              });
          }

          // Start refresh process
          isRefreshing.current = true;
          console.log("🔄 Starting token refresh process");

          try {
            const newAccessToken = await refreshTokenFn();

            if (newAccessToken) {
              console.log("✅ Processing queued requests with new token");
              processQueue(null, newAccessToken);

              // Retry original request with new token
              originalRequest.headers.Authorization = `Bearer ${newAccessToken}`;
              console.log("✅ Retrying original request");
              return axiosInstance(originalRequest);
            } else {
              throw new Error("Token refresh returned null");
            }
          } catch (refreshError) {
            console.error("❌ Token refresh failed, logging out");

            processQueue(refreshError, null);

            setSession(null);
            dispatch({ type: Types.Logout });

            if (window.location.pathname !== "/login") {
              window.location.href = "/login";
            }

            return Promise.reject(refreshError);
          } finally {
            isRefreshing.current = false;
            console.log("✅ Token refresh process complete");
          }
        }

        return Promise.reject(error);
      }
    );

    return () => {
      axiosInstance.interceptors.response.eject(interceptor);
    };
  }, []);

  if (!state.isInitialized) {
    return <LoadingScreen />;
  }

  return (
    <AuthContext.Provider
      value={{
        ...state,
        method: "JWT",
        loginWithEmailPassword,
        requestOtp,
        loginWithOtp,
        logout,
        refreshUser,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;







// import jwtDecode from "jwt-decode";
// import {
//   createContext,
//   ReactNode,
//   useEffect,
//   useReducer,
//   useRef,
//   useState,
// } from "react";
// import LoadingScreen from "../components/LoadingScreen";
// import axiosInstance from "../api";
// import axios from "axios";
// import { ACCESS_TOKEN_KEY, API_BASE_URL, REFRESH_TOKEN_KEY, USER_KEY } from "../api/constants";

// export type ActionMap<M extends { [index: string]: any }> = {
//   [Key in keyof M]: M[Key] extends undefined
//     ? {
//         type: Key;
//       }
//     : {
//         type: Key;
//         payload: M[Key];
//       };
// };

// export type AuthUser = null | Record<string, any>;

// export type AuthState = {
//   isAuthenticated: boolean;
//   isInitialized: boolean;
//   user: AuthUser;
// };

// enum Types {
//   Init = "INIT",
//   Login = "LOGIN",
//   Logout = "LOGOUT",
//   UpdateUser = "UPDATE_USER",
// }

// type JWTAuthPayload = {
//   [Types.Init]: {
//     isAuthenticated: boolean;
//     user: AuthUser;
//   };
//   [Types.Logout]: undefined;
//   [Types.Login]: { user: AuthUser };
//   [Types.UpdateUser]: { user: AuthUser };
// };

// type JWTActions = ActionMap<JWTAuthPayload>[keyof ActionMap<JWTAuthPayload>];

// type TAuthContext = {
//   user?: AuthUser | null;
//   isInitialized?: boolean;
//   isAuthenticated?: boolean;
//   method?: string;
//   loginWithEmailPassword?: (email: string, password: string) => Promise<void>;
//   requestOtp?: (phone_number: string, purpose: string) => Promise<void>;
//   loginWithOtp?: (phone_number: string, otp_code: string) => Promise<void>;
//   logout?: () => void;
//   refreshUser?: () => Promise<void>;
// };

// const initialState: AuthState = {
//   user: null,
//   isInitialized: false,
//   isAuthenticated: false,
// };

// const isValidToken = (accessToken: string) => {
//   if (!accessToken) return false;
//   try {
//     const decodedToken = jwtDecode<{ exp: number }>(accessToken);
//     const currentTime = Date.now() / 1000;
//     return decodedToken.exp > currentTime;
//   } catch (error) {
//     console.error("Invalid token:", error);
//     return false;
//   }
// };

// // Updated to use consistent key names (access_token, refresh_token)
// const setSession = (accessToken: string | null, refreshToken: string | null = null) => {
//   if (accessToken) {
//     localStorage.setItem(ACCESS_TOKEN_KEY, accessToken);
//     axiosInstance.defaults.headers.common.Authorization = `Bearer ${accessToken}`;
//     if (refreshToken) {
//       localStorage.setItem(REFRESH_TOKEN_KEY, refreshToken);
//     }
//   } else {
//     localStorage.removeItem(ACCESS_TOKEN_KEY);
//     localStorage.removeItem(REFRESH_TOKEN_KEY);
//     localStorage.removeItem(USER_KEY);
//     delete axiosInstance.defaults.headers.common.Authorization;
//   }
// };

// const reducer = (state: AuthState, action: JWTActions) => {
//   switch (action.type) {
//     case "INIT": {
//       return {
//         isInitialized: true,
//         user: action.payload.user,
//         isAuthenticated: action.payload.isAuthenticated,
//       };
//     }
//     case "LOGIN": {
//       return {
//         ...state,
//         isAuthenticated: true,
//         user: action.payload.user,
//       };
//     }
//     case "UPDATE_USER": {
//       return {
//         ...state,
//         user: action.payload.user,
//       };
//     }
//     case "LOGOUT": {
//       return {
//         ...state,
//         user: null,
//         isAuthenticated: false,
//       };
//     }
//     default: {
//       return state;
//     }
//   }
// };

// const AuthContext = createContext<TAuthContext>({
//   user: null,
//   isInitialized: false,
//   isAuthenticated: false,
//   method: "JWT",
//   loginWithEmailPassword: () => Promise.resolve(),
//   requestOtp: () => Promise.resolve(),
//   loginWithOtp: () => Promise.resolve(),
//   logout: () => {},
//   refreshUser: () => Promise.resolve(),
// });

// type AuthProviderProps = {
//   children: ReactNode;
// };

// export const AuthProvider = ({ children }: AuthProviderProps) => {
//   const [state, dispatch] = useReducer(reducer, initialState);
//   const [, setGetUserDetails] = useState(false);
//   const isRefreshing = useRef(false);
//   const failedQueue = useRef<any[]>([]);

//   const processQueue = (error: any, token: string | null = null) => {
//     failedQueue.current.forEach((prom) => {
//       if (error) {
//         prom.reject(error);
//       } else {
//         prom.resolve(token);
//       }
//     });
//     failedQueue.current = [];
//   };

//   const refreshTokenFn = async () => {
//     try {
//       const refreshToken = localStorage.getItem(REFRESH_TOKEN_KEY);
//       if (!refreshToken) {
//         throw new Error("No refresh token available");
//       }
      
//       const response = await axios.post(`${API_BASE_URL}auth/token/refresh/`, {
//         refresh: refreshToken,
//       });
      
//       const { access } = response.data;
      
//       if (!access) {
//         throw new Error("No access token in refresh response");
//       }
      
//       // Update session with new token
//       setSession(access, refreshToken);
//       return access;
//     } catch (error) {
//       console.error("Token refresh failed:", error);
//       setSession(null);
//       throw error;
//     }
//   };

//   // New method for email/password login
//   const loginWithEmailPassword = async (email: string, password: string) => {
//     try {
//       const response = await axios.post(`${API_BASE_URL}auth/login/`, {
//         email,
//         password,
//       });

//       const { tokens, user } = response.data;
//       setSession(tokens.access, tokens.refresh);
      
//       // Store user in localStorage
//       localStorage.setItem(USER_KEY, JSON.stringify(user));

//       dispatch({
//         type: Types.Login,
//         payload: { user },
//       });
//       setGetUserDetails(true);
//     } catch (error: any) {
//       throw new Error(error.response?.data?.detail || "Login failed");
//     }
//   };

//   const requestOtp = async (phone_number: string, purpose: string) => {
//     try {
//       await axios.post(`${API_BASE_URL}auth/request-otp/`, {
//         phone_number,
//         purpose,
//       });
//     } catch (error: any) {
//       throw new Error(error.response?.data?.message || "Failed to request OTP");
//     }
//   };

//   const loginWithOtp = async (phone_number: string, otp_code: string) => {
//     try {
//       const response = await axios.post(`${API_BASE_URL}auth/login/`, {
//         phone_number,
//         otp_code,
//       });

//       const { access, refresh, user } = response.data;
//       setSession(access, refresh);
//       localStorage.setItem(USER_KEY, JSON.stringify(user));

//       dispatch({
//         type: Types.Login,
//         payload: { user },
//       });
//       setGetUserDetails(true);
//     } catch (error: any) {
//       throw new Error(error.response?.data?.message || "Login failed");
//     }
//   };

//   const logout = () => {
//     setSession(null);
//     setGetUserDetails(false);
//     dispatch({ type: Types.Logout });
//   };

//   const getUser = async (accessToken: string): Promise<any> => {
//     try {
//       // First check if user is stored in localStorage
//       const storedUser = localStorage.getItem(USER_KEY);
//       if (storedUser) {
//         return JSON.parse(storedUser);
//       }

//       // Otherwise fetch from API
//       const decodedToken: any = jwtDecode<{ user_id: string }>(accessToken);
//       const userId = decodedToken.user_id;

//       const response = await axiosInstance.get(`/auth/users/${userId}/`);
//       const user = response.data;
      
//       // Store for future use
//       localStorage.setItem(USER_KEY, JSON.stringify(user));
//       return user;
//     } catch (error: any) {
//       console.error("Failed to fetch user:", error);
//       return {};
//     }
//   };

//   // New refreshUser function
//   const refreshUser = async () => {
//     try {
//       const accessToken = window.localStorage.getItem(ACCESS_TOKEN_KEY);
//       if (!accessToken || !isValidToken(accessToken)) {
//         console.error("No valid access token available");
//         return;
//       }

//       // Fetch current user profile from /me endpoint
//       const response = await axiosInstance.get('/auth/users/me/');
//       const user = response.data;

//       // Update localStorage
//       localStorage.setItem(USER_KEY, JSON.stringify(user));

//       // Update context state
//       dispatch({
//         type: Types.UpdateUser,
//         payload: { user },
//       });
//     } catch (error: any) {
//       console.error("Failed to refresh user:", error);
//       // If refresh fails due to auth error, try to refresh token
//       if (error.response?.status === 401) {
//         const newAccessToken = await refreshTokenFn();
//         if (newAccessToken) {
//           // Retry fetching user
//           try {
//             const response = await axiosInstance.get('/auth/users/me/');
//             const user = response.data;
//             localStorage.setItem(USER_KEY, JSON.stringify(user));
//             dispatch({
//               type: Types.UpdateUser,
//               payload: { user },
//             });
//           } catch (retryError) {
//             console.error("Failed to refresh user after token refresh:", retryError);
//             // If still failing, logout
//             logout();
//           }
//         } else {
//           // Token refresh failed, logout
//           logout();
//         }
//       }
//     }
//   };

//   useEffect(() => {
//     (async () => {
//       try {
//         const accessToken = window.localStorage.getItem(ACCESS_TOKEN_KEY);

//         if (accessToken && isValidToken(accessToken)) {
//           const user = await getUser(accessToken);
//           dispatch({
//             type: Types.Init,
//             payload: {
//               user,
//               isAuthenticated: true,
//             },
//           });
//         } else if (localStorage.getItem(REFRESH_TOKEN_KEY)) {
//           // Attempt to refresh the access token
//           const newAccessToken = await refreshTokenFn();
//           if (newAccessToken) {
//             const user = await getUser(newAccessToken);
//             dispatch({
//               type: Types.Init,
//               payload: {
//                 user,
//                 isAuthenticated: true,
//               },
//             });
//           } else {
//             dispatch({
//               type: Types.Init,
//               payload: {
//                 user: null,
//                 isAuthenticated: false,
//               },
//             });
//           }
//         } else {
//           dispatch({
//             type: Types.Init,
//             payload: {
//               user: null,
//               isAuthenticated: false,
//             },
//           });
//         }
//       } catch (err) {
//         console.error("Initialization error:", err);
//         setSession(null);
//         dispatch({
//           type: Types.Init,
//           payload: {
//             user: null,
//             isAuthenticated: false,
//           },
//         });
//       }
//     })();
//   }, []);

//   // Token refresh interceptor with queue management
//   useEffect(() => {
//     const interceptor = axiosInstance.interceptors.response.use(
//       (response) => response,
//       async (error) => {
//         const originalRequest = error.config;

//         // Don't retry if:
//         // 1. Already retried
//         // 2. Request is to login/refresh endpoint
//         // 3. No error response
//         if (
//           !error.response ||
//           originalRequest._retry ||
//           originalRequest.url?.includes('/auth/login') ||
//           originalRequest.url?.includes('/auth/token/refresh')
//         ) {
//           return Promise.reject(error);
//         }

//         // Only handle 401 errors
//         if (error.response.status === 401) {
//           // Check if we have a refresh token
//           const refreshToken = localStorage.getItem(REFRESH_TOKEN_KEY);
          
//           if (!refreshToken) {
//             console.log("No refresh token, logging out");
//             setSession(null);
//             dispatch({ type: Types.Logout });
            
//             if (window.location.pathname !== "/login") {
//               window.location.href = "/login";
//             }
//             return Promise.reject(error);
//           }

//           // Mark request as retried
//           originalRequest._retry = true;

//           // If already refreshing, queue this request
//           if (isRefreshing.current) {
//             return new Promise((resolve, reject) => {
//               failedQueue.current.push({ resolve, reject });
//             })
//               .then((token) => {
//                 originalRequest.headers.Authorization = `Bearer ${token}`;
//                 return axiosInstance(originalRequest);
//               })
//               .catch((err) => {
//                 return Promise.reject(err);
//               });
//           }

//           // Start refresh process
//           isRefreshing.current = true;

//           try {
//             const newAccessToken = await refreshTokenFn();
            
//             if (newAccessToken) {
//               // Process queued requests
//               processQueue(null, newAccessToken);
              
//               // Retry original request with new token
//               originalRequest.headers.Authorization = `Bearer ${newAccessToken}`;
//               return axiosInstance(originalRequest);
//             } else {
//               throw new Error("Token refresh returned null");
//             }
//           } catch (refreshError) {
//             console.error("Token refresh failed:", refreshError);
            
//             // Process queue with error
//             processQueue(refreshError, null);
            
//             // Clear session and logout
//             setSession(null);
//             dispatch({ type: Types.Logout });
            
//             if (window.location.pathname !== "/login") {
//               window.location.href = "/login";
//             }
            
//             return Promise.reject(refreshError);
//           } finally {
//             isRefreshing.current = false;
//           }
//         }

//         return Promise.reject(error);
//       }
//     );

//     return () => {
//       axiosInstance.interceptors.response.eject(interceptor);
//     };
//   }, []);

//   if (!state.isInitialized) {
//     return <LoadingScreen />;
//   }

//   return (
//     <AuthContext.Provider
//       value={{
//         ...state,
//         method: "JWT",
//         loginWithEmailPassword,
//         requestOtp,
//         loginWithOtp,
//         logout,
//         refreshUser,
//       }}
//     >
//       {children}
//     </AuthContext.Provider>
//   );
// };

// export default AuthContext;